import React, { useState, useCallback, useEffect } from "react";
import {
  ArrowBigRight,
  Building,
  Calendar,
  FileSliders,
  Lightbulb,
  RefreshCcw,
  Settings,
  X,
} from "lucide-react";

import BlogCard from "./BlogCard";
import { useFunctionContext } from "../../context/FunctionalContext";
import DottedLoading from "../../common/DottedLoading";
import RealtyNewsLetter from "./common/RealtyNewsLetter";
import { useNavigate } from "react-router-dom";

const Blog = () => {
  const [rotating, setRotating] = useState(false);

  const navigate = useNavigate();

  const { posts, postLoading, postHasMore, fetchPosts } = useFunctionContext();

  const handleLoadMore = () => {
    if (postLoading || !postHasMore) return;

    setRotating(true);
    fetchPosts(posts.length, true).finally(() => {
      setRotating(false);
      setTimeout(() => {
        window.scrollBy({ top: 400, behavior: "smooth" });
      }, 300);
    });
  };
  const handleRefresh = () => {
    setRotating(true);
    fetchPosts(0, true).finally(() => {
      setRotating(false);
    });
  };

  return (
    <>
      <section className="relative h-[70vh] md:h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-r from-gold/10 to-black text-white py-10 sm:py-16 lg:pt-36 lg:pb-24 ">
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: 'url("/images/realty/16.jpg")',
            backgroundAttachment: "fixed",
            backgroundSize: "cover",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        </div>
        <div className="relative z-10 text-center px-4 md:px-10 animate-fade-in mt-14 lg:mt-0">
          <Building className="md:w-16 md:h-16 w-12 h-12 text-white mx-auto mb-6" />
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4">
            News & Articles
          </h1>
          <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto">
            Stay updated with the latest news, expert insights, and helpful tips
            in the real estate world. Explore articles that guide you through
            market trends, property tips, and much more.
          </p>
        </div>
      </section>

      <section className="pt-10 lg:pt-20 md:pt-16">
        <div className="container max-w-6xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 md:mb-0">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Subscribe for Exclusive Properties
              </h2>
              <p className="text-gray-400 max-w-lg">
                Discover our handpicked selection of exceptional properties from
                around the world.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <RealtyNewsLetter />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <div className="max-w-[85rem] px-4 py-6 sm:px-6 lg:px-8 lg:py-24 lg:pt-32 mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gold mb-4">
            Featured Blog Post
          </h2>
          <p className="text-gray-400 text-lg">
            Comprehensive real estate solutions tailored to your unique needs
          </p>
        </div>
        <div className="relative p-5 md:p-10">
          {/* Grid */}
          <div className="relative z-10 lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
            <div className="mb-10 lg:mb-0 lg:col-span-6 lg:col-start-8 lg:order-2">
              <h2 className="text-2xl  font-bold sm:text-3xl text-neutral-200">
                Fully customizable rules to match your unique needs
              </h2>

              {/* Tab Navs */}
              <nav
                className="grid gap-4 mt-5 md:mt-10"
                aria-label="Tabs"
                role="tablist"
                aria-orientation="vertical"
              >
                <button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/realty/blog/${posts[0]?.posting_id || ""}`);
                  }}
                  type="button"
                  className=" hs-tab-active:shadow-md hs-tab-active:hover:border-transparent text-start  focus:outline-hidden  p-4 md:p-5 rounded-xl hs-tab-active:bg-neutral-700 hover:bg-neutral-700 focus:bg-neutral-700 active"
                  id="tabs-with-card-item-1"
                  aria-selected="true"
                  data-hs-tab="#tabs-with-card-1"
                  aria-controls="tabs-with-card-1"
                  role="tab"
                >
                  <span className="flex gap-x-6">
                    <svg
                      className="shrink-0 mt-2 size-6 md:size-7 hs-tab-active:text-gold text-neutral-200"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 5.5A3.5 3.5 0 0 1 8.5 2H12v7H8.5A3.5 3.5 0 0 1 5 5.5z" />
                      <path d="M12 2h3.5a3.5 3.5 0 1 1 0 7H12V2z" />
                      <path d="M12 12.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 1 1-7 0z" />
                      <path d="M5 19.5A3.5 3.5 0 0 1 8.5 16H12v3.5a3.5 3.5 0 1 1-7 0z" />
                      <path d="M5 12.5A3.5 3.5 0 0 1 8.5 9H12v7H8.5A3.5 3.5 0 0 1 5 12.5z" />
                    </svg>
                    <span className="grow">
                      <span className="block text-lg font-semibold hs-tab-active:text-gold text-neutral-200">
                        Here’s how decorate your new home from scratch
                      </span>
                      <span className="block mt-1  hs-tab-active:text-gray-200 text-neutral-200">
                        Design your space with style and purpose, your way.{" "}
                      </span>
                    </span>
                  </span>
                </button>

                <button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/realty/blog/${posts[1]?.posting_id || ""}`);
                  }}
                  type="button"
                  className="hs-tab-active:shadow-md hs-tab-active:hover:border-transparent text-start  focus:outline-hidden  p-4 md:p-5 rounded-xl hs-tab-active:bg-neutral-700 hover:bg-neutral-700 focus:bg-neutral-700"
                  id="tabs-with-card-item-2"
                  aria-selected="false"
                  data-hs-tab="#tabs-with-card-2"
                  aria-controls="tabs-with-card-2"
                  role="tab"
                >
                  <span className="flex gap-x-6">
                    <svg
                      className="shrink-0 mt-2 size-6 md:size-7 hs-tab-active:text-gold text-neutral-200"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m12 14 4-4" />
                      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
                    </svg>
                    <span className="grow">
                      <span className="block text-lg font-semibold hs-tab-active:text-gold text-neutral-200">
                        Home buying basics: How many bedrooms and bathrooms?
                      </span>
                      <span className="block mt-1  hs-tab-active:text-gray-200 text-neutral-200">
                        What to look for when buying a home.
                      </span>
                    </span>
                  </span>
                </button>

                <button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/realty/blog/${posts[2]?.posting_id || ""}`);
                  }}
                  type="button"
                  className="hs-tab-active:shadow-md hs-tab-active:hover:border-transparent text-start  focus:outline-hidden  p-4 md:p-5 rounded-xl hs-tab-active:bg-neutral-700 hover:bg-neutral-700 focus:bg-neutral-700"
                  id="tabs-with-card-item-3"
                  aria-selected="false"
                  data-hs-tab="#tabs-with-card-3"
                  aria-controls="tabs-with-card-3"
                  role="tab"
                >
                  <span className="flex gap-x-6">
                    <svg
                      className="shrink-0 mt-2 size-6 md:size-7 hs-tab-active:text-gold text-neutral-200"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                      <path d="M5 3v4" />
                      <path d="M19 17v4" />
                      <path d="M3 5h4" />
                      <path d="M17 19h4" />
                    </svg>
                    <span className="grow">
                      <span className="block text-lg font-semibold hs-tab-active:text-gold text-neutral-200">
                        Powerful features
                      </span>
                      <span className="block mt-1  hs-tab-active:text-gray-200 text-neutral-200">
                        Reduce time and effort on building modern look design
                        with Preline only.
                      </span>
                    </span>
                  </span>
                </button>
              </nav>
              {/* End Tab Navs */}
            </div>
            {/* End Col */}

            <div className="lg:col-span-6 hidden lg:block">
              <div className="relative">
                {/* Tab Content */}
                <div>
                  <div
                    id="tabs-with-card-1"
                    role="tabpanel"
                    aria-labelledby="tabs-with-card-item-1"
                  >
                    <img
                      className="shadow-xl h-60 sm:h-72 md:h-80 lg:h-96 rounded-xl shadow-gray-900/20"
                      // src="/images/realty/7.jpg"
                      src={`${posts[0]?.images[0].url || ""}`}
                      alt="Features Image"
                    />
                  </div>

                  <div
                    id="tabs-with-card-2"
                    className="hidden"
                    role="tabpanel"
                    aria-labelledby="tabs-with-card-item-2"
                  >
                    <img
                      className="shadow-xl h-96 rounded-xl shadow-gray-900/20"
                      // src="/images/realty/8.jpg"
                      src={`${posts[1]?.images[0].url || ""}`}
                      alt="Features Image"
                    />
                  </div>

                  <div
                    id="tabs-with-card-3"
                    className="hidden"
                    role="tabpanel"
                    aria-labelledby="tabs-with-card-item-3"
                  >
                    <img
                      className="shadow-xl  rounded-xl shadow-gray-900/20"
                      // src="/images/realty/5.jpg"
                      src={`${posts[2]?.images[0].url || ""}`}
                      alt="Features Image"
                    />
                  </div>
                </div>
                {/* End Tab Content */}

                {/* SVG Element */}
                <div className="hidden absolute top-0 end-0 translate-x-20 md:block lg:translate-x-20">
                  <svg
                    className="w-16 h-auto text-orange-500"
                    width="121"
                    height="135"
                    viewBox="0 0 121 135"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 16.4754C11.7688 27.4499 21.2452 57.3224 5 89.0164"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeLinecap="round"
                    />
                    <path
                      d="M33.6761 112.104C44.6984 98.1239 74.2618 57.6776 83.4821 5"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeLinecap="round"
                    />
                    <path
                      d="M50.5525 130C68.2064 127.495 110.731 117.541 116 78.0874"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                {/* End SVG Element */}
              </div>
            </div>
            {/* End Col */}
          </div>
          {/* End Grid */}

          {/* Background Color */}
          <div className="absolute inset-0 grid grid-cols-12 size-full">
            <div className="col-span-full lg:col-span-7 lg:col-start-6  w-full rounded-xl sm:h-3/4 md:h-full lg:h-full bg-neutral-800"></div>
          </div>
          {/* End Background Color */}
        </div>
      </div>
      {/* End Features */}

      <section className="py-10 lg:pt-20 lg:pb-32 md:py-16 container">
        <div className="mx-auto sm:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold leading-tight md:text-3xl lg:text-5xl">
              Latest blogs
            </h2>
            <p className="max-w-xl mx-auto mt-3 text-base lg:text-lg leading-relaxed text-gray-400">
              Stay updated with our most recent stories and insights.
            </p>
          </div>

          {posts?.length > 0 ? (
            <div
              className="relative group ms-auto my-5 float-end inline-flex gap-1 bg-slate-900 rounded-lg p-2 px-4 justify-end cursor-pointer"
              onClick={!rotating ? handleRefresh : null}
            >
              Refresh
              <RefreshCcw className={rotating ? "animate-spin" : ""} />
              {/* Tooltip */}
              <span className="absolute bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition text-nowrap">
                Refresh for latest
              </span>
            </div>
          ) : null}

          <div className="grid grid-cols-1 mt-10 md:grid-cols-2 lg:mt-16 lg:grid-cols-3 gap-4 lg:gap-x-8 lg:gap-y-12 w-full">
            {postLoading ? (
              Array.from({ length: posts.length || 3 }).map((_, index) => (
                <div
                  key={index}
                  className="relative transition-all duration-300 transform"
                >
                  <div className="w-[276px] lg:w-[336px] rounded-xl bg-black p-3 animate-pulse">
                    <div className="relative w-full h-[180px] bg-gray-800 rounded-lg">
                      <div className="absolute top-2 left-2 h-6 w-14 bg-gray-700 rounded-full"></div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="h-4 bg-gray-700 rounded w-4/5"></div>
                      <div className="h-4 bg-gray-700 rounded w-3/5"></div>
                    </div>

                    <div className="mt-4 flex items-center justify-between text-sm">
                      <div className="h-4 w-20 bg-gray-700 rounded"></div>
                      <div className="h-4 w-16 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : !posts || posts.length === 0 ? (
              <div className="text-center text-gray-400 py-10 w-full col-span-full">
                🚫 No blogs available.
              </div>
            ) : (
              posts.map((post) => (
                <BlogCard key={post.posting_id} post={post} />
              ))
            )}
          </div>
          {/* {postHasMore && (
            <div className="flex justify-center mt-10">
              <button
                onClick={handleLoadMore}
                disabled={postLoading}
                className="bg-white text-black px-5 py-1.5 rounded transition-all duration-300 hover:bg-white/85 disabled:opacity-70"
              >
                {postLoading ? (
                  <DottedLoading text={"Loading posts..."} />
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )} */}
          {postHasMore && !postLoading && (
            <div className="flex justify-center mt-10">
              <button
                onClick={handleLoadMore}
                className="bg-white text-black px-5 py-1.5 rounded transition-all duration-300 hover:bg-white/85 disabled:opacity-70"
              >
                Load More
              </button>
            </div>
          )}

          {postLoading && (
            <div className="flex justify-center mt-10">
              <DottedLoading text="Loading posts..." />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;
